import React from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  Avatar,
  Divider,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
} from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { formatDistanceToNow } from "date-fns";

const Profile: React.FC = () => {
  const { user, isLoading } = useAuth0();

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box py={4} textAlign="center">
          <Typography variant="h5">Loading profile...</Typography>
        </Box>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="lg">
        <Box py={4} textAlign="center">
          <Typography variant="h5">
            You need to be logged in to view your profile.
          </Typography>
        </Box>
      </Container>
    );
  }

  // Format the update time if available
  const updatedAt = user.updated_at
    ? formatDistanceToNow(new Date(user.updated_at), { addSuffix: true })
    : "unknown";

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        <Typography variant="h4" gutterBottom>
          My Profile
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          View and manage your profile information
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Paper elevation={1} sx={{ p: 3, height: "100%" }}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                mb={3}
              >
                <Avatar
                  src={user.picture}
                  alt={user.name || "Profile"}
                  sx={{ width: 120, height: 120, mb: 2 }}
                />
                <Typography variant="h5" gutterBottom>
                  {user.name}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  {user.email}
                </Typography>
                {user.email_verified && (
                  <Chip
                    label="Email Verified"
                    color="success"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Last updated: {updatedAt}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  User ID: {user.sub}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Stack spacing={3}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    User Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Given Name
                      </Typography>
                      <Typography variant="body1">
                        {user.given_name || "Not provided"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Family Name
                      </Typography>
                      <Typography variant="body1">
                        {user.family_name || "Not provided"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Nickname
                      </Typography>
                      <Typography variant="body1">
                        {user.nickname || "Not provided"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Locale
                      </Typography>
                      <Typography variant="body1">
                        {user.locale || "Not provided"}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {user.identities && (
                <Card elevation={1}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Connected Accounts
                    </Typography>
                    {user.identities.map((identity: any) => (
                      <Box
                        key={identity.provider}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ textTransform: "capitalize" }}
                          >
                            {identity.provider}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {identity.connection}
                          </Typography>
                        </Box>
                        <Chip
                          label={identity.isSocial ? "Social" : "Database"}
                          color={identity.isSocial ? "primary" : "default"}
                          size="small"
                        />
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              )}

              <Card elevation={1}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h6">Account Settings</Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        // TODO: Implement settings update
                        alert("This feature is not yet implemented");
                      }}
                    >
                      Update Settings
                    </Button>
                  </Box>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Manage your account settings and preferences
                  </Typography>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Profile;
